import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Tag, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Topics = () => {
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [catsRes, postsRes] = await Promise.all([
          axios.get(`${API_URL}/api/categories`),
          axios.get(`${API_URL}/api/posts`),
        ]);
        setCategories(catsRes.data);
        setPosts(postsRes.data);
      } catch (e) {
        console.error(e);
        setError('Failed to load topics');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="topics-loading">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  const allTags = Array.from(new Set(posts.flatMap(p => p.tags || [])));

  return (
    <div className="min-h-screen pt-32 pb-24 px-6" data-testid="topics-page">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-light mb-6">Topics</h1>
          <p className="text-xl text-gray-600">Explore articles by category</p>
        </div>

        {error && <p className="text-center text-red-600 mb-8">{error}</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-24">
          {categories.map(category => {
            const categoryPosts = posts.filter(p => p.categorySlug === category.slug);
            return (
              <div key={category.slug} id={category.slug} className="bg-gray-50 p-8 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <Tag className="text-gray-600" size={24} />
                  <span className="text-sm text-gray-600">{category.count} articles</span>
                </div>
                <h2 className="text-2xl font-light mb-4">{category.name}</h2>
                <ul className="space-y-3">
                  {categoryPosts.slice(0, 3).map(post => (
                    <li key={post.id}>
                      <Link to={`/post/${post.slug}`} className="text-gray-700 hover:text-black transition-colors line-clamp-2">
                        {post.title}
                      </Link>
                    </li>
                  ))}
                </ul>
                {categoryPosts.length > 3 && (
                  <Link to="/archive" className="inline-block mt-4 text-sm text-gray-600 hover:text-black transition-colors">
                    View all {category.count} articles →
                  </Link>
                )}
              </div>
            );
          })}
        </div>

        {allTags.length > 0 && (
          <div className="bg-gray-50 p-12 rounded-lg">
            <h2 className="text-3xl font-light mb-8 text-center">Popular Tags</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {allTags.map((tag, i) => (
                <span key={i} className="px-4 py-2 bg-white text-gray-700 rounded-full text-sm hover:bg-gray-200 transition-colors cursor-pointer">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Topics;
